import type { ReactElement } from 'react';
import dynamic from 'next/dynamic';
import type { URLManager as URLManagerSchema } from '@amplience/content-types/typings/h-url-manager';
import { createAppointmentAmplienceComponent } from '@storefront/lib-appointments/components/appointmentsAmplienceComponents.utils';
import { createContentAmplienceComponent } from '@storefront/lib-content/components/contentAmplienceComponents.utils';
import type { BreadcrumbDataType } from '@storefront/lib-content/components/contentBreadcrumb';
import { CmsSlot, useLocaleContext } from '@storefront/lib-global';
import { createGlobalAmplienceComponent } from '@storefront/lib-global/globalComponents/globalAmplienceComponents.utils';
import type { HandlebarTemplateProps } from '@storefront/lib-global/globalComponents/handlebarTemplate';
import type { AmplienceContext } from '@storefront/lib-global/types/amplience';
import { CMS_SLOTS_DELIVERY_KEYS } from '@storefront/lib-global/utils/amplience/constants';
import { renderAmplienceSlotComponents } from '@storefront/lib-global/utils/amplienceUtils';
import { createPDPAmplienceComponent } from '@storefront/lib-products/product-detail/components/pdpAmplienceComponents.utils';

/* eslint-disable promise/prefer-await-to-then */
const HTMLContent = dynamic(() =>
  import(
    /* webpackChunkName: "HTMLContent" */ '@storefront/lib-content/components/htmlContentComponent/HTMLContent'
  ).then((mod) => mod.HTMLContent),
);

const HygienePageLayout = dynamic(() =>
  import(
    /* webpackChunkName: "HygienePageLayout" */ '@storefront/lib-content/components/hygienePages/HygienePageLayout'
  ).then((mod) => mod.HygienePageLayout),
);

const ContentBreadcrumb = dynamic(() =>
  import(
    /* webpackChunkName: "ContentBreadcrumb" */ '@storefront/lib-content/components/contentBreadcrumb/ContentBreadcrumb'
  ).then((mod) => mod.ContentBreadcrumb),
);
/* eslint-enable promise/prefer-await-to-then */

export type URLManagerProps = URLManagerSchema & {
  context: AmplienceContext;
  breadcrumbData: BreadcrumbDataType[];
  handlebarComponents: HandlebarTemplateProps[] | null;
};

export type ContentProps = {
  content: URLManagerProps['content'];
  context: URLManagerProps['context'];
  locale: string;
  breadcrumbData: BreadcrumbDataType[];
  handlebarComponents: HandlebarTemplateProps[] | null;
};

function Content({ content, breadcrumbData, handlebarComponents, locale }: ContentProps): ReactElement | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const contents = content as unknown as any;

  if (!content) {
    return null;
  }

  const isHygienePageLayout = contents[0]._meta.schema === 'https://clarks.com/slot/s-hygiene-page-layout.json';
  const isHtmlLayout = contents[0]._meta.schema === 'https://clarks.com/slot/s-html-layout.json';

  return (
    <>
      {breadcrumbData?.length ? <ContentBreadcrumb breadcrumbData={breadcrumbData} /> : null}
      <CmsSlot deliveryKey={CMS_SLOTS_DELIVERY_KEYS.CMS_TOP_BANNER} />
      {isHtmlLayout ? <HTMLContent {...contents[0]} /> : null}
      <div>
        {!isHygienePageLayout ? (
          contents[0].slotContent &&
          renderAmplienceSlotComponents(
            contents[0].slotContent.pageContent,
            {
              ...createGlobalAmplienceComponent(contents[0].slotContent.pageContent),
              ...createPDPAmplienceComponent(contents[0].slotContent.pageContent),
              ...createContentAmplienceComponent(contents[0].slotContent.pageContent),
              ...createAppointmentAmplienceComponent(contents[0].slotContent.pageContent),
            },
            handlebarComponents,
            locale,
          )
        ) : (
          <HygienePageLayout {...contents[0]} handlebarComponents={handlebarComponents} />
        )}
      </div>
    </>
  );
}
export function URLManager({ content, context, breadcrumbData, handlebarComponents }: URLManagerProps): ReactElement {
  const { locale } = useLocaleContext();

  return (
    <Content
      content={content}
      context={context}
      locale={locale}
      breadcrumbData={breadcrumbData}
      handlebarComponents={handlebarComponents}
    />
  );
}
